import { useEffect, useRef } from 'react';
import { History, Users, Award, ArrowRight, Building2, Calendar, User, Edit, UserPlus, Building, Rocket, Mail, Phone, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { team } from '../constants/data';
import { motion } from 'framer-motion';

import heroBackground from '../assets/fphc_cave.jpeg';

const About = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px 0px -100px 0px'
    });

    document.querySelectorAll('.animate-on-scroll').forEach((element) => {
      observerRef.current?.observe(element);
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  const milestones = [
      {
      year: 2011,
      title: 'Gründung',
      description: 'Errichtung der "FPHC Handels- und Consulting GmbH" in Linz durch Herrn DI (FH) Florian Pauk.',
      icon: User,
      color: 'from-[#B5E2BF] to-[#009E60]'
      },
      {
      year: 2012,
      title: 'Namensänderung',
      description: 'Änderung des Firmenwortlautes in "FPHC UmweltConsulting GmbH"',
      icon: Edit,
      color: 'from-[#009E60] to-[#007A5E]'
      },
      {
      year: 2013,
      title: 'Gesellschaftserweiterung',
      description: 'Eintritt von Frau DI (FH) Birgit Pauk als Mitgesellschafterin',
      icon: UserPlus,
      color: 'from-[#75B2DD] to-[#009E60]'
      },
      {
      year: 2015,
      title: 'Standortwechsel',
      description: 'Firmensitzwechsel / Übersiedelung nach Leonding',
      icon: Building2,
      color: 'from-[#009E60] to-[#007A5E]'
      },
      {
      year: 2019,
      title: 'Erstakkreditierung',
      description: 'Typ-A-Inspektionsstelle gem. EN ISO/IEC 17020:2012 zur Durchführung von Inspektionsstätigkeiten nach Deponieverordnung 2008 idgF',
      icon: Award,
      color: 'from-[#B5E2BF] to-[#009E60]'
      },
      {
      year: 2021,
      title: 'Gesellschaftserweiterung',
      description: 'Eintritt von Frau Silvia Pixner, MSc (geb. Gruber) als Mitgesellschafterin',
      icon: UserPlus,
      color: 'from-[#75B2DD] to-[#009E60]'
      }
  ];

  const values = [
    {
      title: 'Expertise',
      description: 'Jahrelange Erfahrung und kontinuierliche Weiterbildung',
      icon: Award
    },
    {
      title: 'Nachhaltigkeit',
      description: 'Umweltbewusstes Handeln in allen Bereichen',
      icon: Building2
    },
    {
      title: 'Kundenzufriedenheit',
      description: 'Laufende Kommunikation mit unseren Kunden',
      icon: Users
    },
    {
      title: 'Arbeitsklima',
      description: 'Respekt, Offenheit und Zusammenhalt',
      icon: History
    }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-[70vh]">
        <div className="absolute inset-0">
          <img 
            src={heroBackground}
            alt="Environmental background"
            className="w-full h-full object-cover brightness-[0.4]"
          />
        </div>

        <div className="relative container mx-auto px-4 py-24 pt-32 pb-32 sm:pb-24">
          <div className="max-w-5xl">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl mb-8 animate-fade-in">
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 bg-[#75B2DD] rounded-[2px] animate-pulse-first" />
                <div className="w-2.5 h-2.5 bg-[#B5E2BF] rounded-[2px] animate-pulse-second" />
                <div className="w-2.5 h-2.5 bg-[#009E60] rounded-[2px] animate-pulse-third" />
              </div>
              <span className="text-success font-medium">Über uns</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <div className="space-y-8 animate-slide-up">
                <div className="space-y-6">
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                    Ihr Partner für <span className="text-[#B5E2BF]">nachhaltige</span> Umwelttechnik
                  </h1>
                  <div className="w-20 h-1.5 bg-gradient-to-r from-success to-success/30 rounded-full" />
                  <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                    Wir stehen Ihnen als kompetenter Partner in allen Bereichen der Abfall- und Kreislaufwirtschaft, Wasserwirtschaft und Umwelttechnik <br className="hidden sm:block" />
                    innerhalb und außerhalb Österreichs zur Seite.
                  </p>
                </div>
              </div>

              <div className="relative pl-0 lg:pl-12 mt-8 lg:mt-0">
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  {values.map((category, index) => {
                    const Icon = category.icon;
                    const isWide = category.title === 'Nachhaltigkeit' || category.title === 'Mitarbeiterzufriedenheit';

                    return (
                      <div
                        key={category.title}
                        className={`group bg-white/5 backdrop-blur-sm rounded-xl transition-all duration-300 overflow-hidden animate-fade-in ${
                          index % 2 === 0 ? 'lg:translate-y-8' : ''} ${
                          isWide ? 'col-span-1 sm:col-span-1 sm:max-w-[200px]' : ''}`}
                        style={{ animationDelay: `${index * 150}ms` }}
                      >
                        <div className="p-4 sm:p-6 h-full w-full transition-colors duration-300">
                          <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-success mb-3 sm:mb-4" />
                          <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">{category.title}</h3>
                          <p className="text-gray-400 text-xs sm:text-sm">{category.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagonal Cut
        <div className="absolute -bottom-1 left-0 w-full h-32 overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-24 sm:h-32 bg-white"
               style={{
                 clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 40%)'
               }}
          />
        </div>
        */}
      </section>

      {/* History Section */}
      <section className="relative bg-white py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 animate-on-scroll">
              <div className="inline-flex items-center space-x-3 bg-[#009E60]/5 px-4 py-2 rounded-lg mb-4">
                <Calendar className="w-5 h-5 text-[#009E60]" />
                <span className="text-[#009E60] font-medium">Unsere Geschichte</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Eine Reise der Innovation</h2>
              <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">
                Wir sind ein Familienunternehmen im Herzen Oberösterreichs. Seit der Gründung im Jahr 2011 leben wir unsere Vision, unsere Kunden durch kontinuierliche Weiterentwicklung einen Mehrwert in allen Fragen der Umwelttechnik zu bieten. 
              </p>
            </div>

            <div className="grid gap-8 sm:gap-12">
              {milestones.map((milestone, index) => {
                const Icon = milestone.icon;
                return (
                  <div
                    key={milestone.year}
                    className="animate-on-scroll"
                    style={{ transitionDelay: `${index * 0.2}s` }}
                  >
                    <div className={`relative flex ${index % 2 === 0 ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row-reverse'} items-center gap-6 sm:gap-8`}>
                      {/* Year Column */}
                      <div className={`w-full sm:w-48 flex-shrink-0 ${index % 2 === 0 ? 'text-center sm:text-right' : 'text-center sm:text-left'}`}>
                        <div className="relative inline-block">
                          <div className={`absolute inset-0 bg-gradient-to-r ${milestone.color} opacity-10 blur-lg transform animate-float`} />
                          <span className="relative text-4xl sm:text-6xl font-bold bg-gradient-to-r from-[#009E60] to-[#007A5E] bg-clip-text text-transparent">
                            {milestone.year}
                          </span>
                        </div>
                      </div>

                      {/* Content Card */}
                      <div className="flex-grow">
                        <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-lg transition-all duration-500 group hover:-translate-y-1">
                          {/* Decorative gradient line */}
                          <div 
                            className={`absolute ${index % 2 === 0 ? 'sm:-left-[2px] left-0 top-0 sm:top-auto h-1.5 sm:h-full w-full sm:w-1.5' : 'sm:-right-[2px] right-0 top-0 sm:top-auto h-1.5 sm:h-full w-full sm:w-1.5'} rounded-full transition-all duration-500 group-hover:scale-110`}
                            style={{
                              background: `linear-gradient(${index % 2 === 0 ? 'to right' : 'to left'}, #009E60, #007A5E)`
                            }}
                          />
                          
                          {/* Icon */}
                          <div className={`relative mb-4 sm:mb-6 transition-transform duration-500 ${index % 2 === 0 ? 'sm:ml-8' : 'sm:ml-0'}`}>
                            <div className={`absolute inset-0 bg-gradient-to-r ${milestone.color} opacity-10 blur-lg group-hover:opacity-20 transition-opacity duration-500`} />
                            <div className="relative w-12 sm:w-14 h-12 sm:h-14 bg-gradient-to-r from-[#009E60] to-[#007A5E] rounded-xl flex items-center justify-center transform group-hover:scale-105 group-hover:rotate-3 transition-all duration-500">
                              <Icon className="w-6 sm:w-7 h-6 sm:h-7 text-white" />
                            </div>
                          </div>

                          <div className={`${index % 2 === 0 ? 'sm:ml-8' : 'sm:ml-0'}`}>
                            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900 transition-colors duration-500 group-hover:text-[#009E60]">
                              {milestone.title}
                            </h3>
                            <p className="text-gray-600 text-sm sm:text-base leading-relaxed transition-colors duration-500 group-hover:text-gray-900">
                              {milestone.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-gray-50 py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
                <Users className="w-5 h-5 text-success" />
                <span className="text-success font-medium">Unser Team</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">Experten für Ihre Umweltprojekte</h2>
              <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">
                Unser erfahrenes Team steht Ihnen mit Fachwissen und Engagement zur Seite.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {team.map((member, index) => (
                <div 
                  key={member.id}
                  className="group bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 animate-on-scroll"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="space-y-6">
                    {/* Name and Role */}
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 group-hover:text-success transition-colors">
                        {member.name}
                      </h3>
                      <p className="text-gray-600 text-sm sm:text-base">{member.role}</p>
                    </div>

                    {/* Contact Information */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      {/* Email */}
                      <a 
                        href={`mailto:${member.email}`}
                        className="flex items-center space-x-3 text-gray-600 hover:text-success transition-colors group/link"
                      >
                        <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-success/5 flex items-center justify-center group-hover/link:bg-success/10 transition-colors">
                          <Mail className="w-4 sm:w-5 h-4 sm:h-5 text-success" />
                        </div>
                        <span className="text-sm">{member.email}</span>
                      </a>

                      {/* Phone (if available) */}
                      {member.phone && (
                        <a 
                          href={`tel:${member.phone}`}
                          className="flex items-center space-x-3 text-gray-600 hover:text-success transition-colors group/link"
                        >
                          <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg bg-success/5 flex items-center justify-center group-hover/link:bg-success/10 transition-colors">
                            <Phone className="w-4 sm:w-5 h-4 sm:h-5 text-success" />
                          </div>
                          <span className="text-sm">{member.phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Join Our Team Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="group relative bg-gradient-to-br from-success/5 via-success/10 to-success/5 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden"
              >
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                    <Plus className="w-8 h-8 text-success group-hover:rotate-180 transition-transform duration-300" />
                  </div>
                  
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-3 text-success">
                      Werden Sie Teil unseres Teams
                    </h3>
                  </div>

                  <Link
                    to="/contact"
                    className="inline-flex items-center space-x-2 bg-success text-white px-6 py-3 rounded-xl hover:bg-dark transition-colors duration-200 group-hover:shadow-lg"
                  >
                    <span>Jetzt bewerben</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                </div>

                {/* Animated background elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                  <motion.div
                    animate={{ 
                      rotate: [0, 360],
                      scale: [0.8, 1.2, 0.8],
                      opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{
                      duration: 15,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    className="absolute -top-1/2 -right-1/2 w-full h-full bg-success/5 rounded-full blur-xl"
                  />
                  <motion.div
                    animate={{ 
                      rotate: [360, 0],
                      scale: [1, 1.5, 1],
                      opacity: [0.2, 0.4, 0.2],
                    }}
                    transition={{
                      duration: 12,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-success/5 rounded-full blur-xl"
                  />
                  <motion.div
                    animate={{ 
                      x: [-20, 20, -20],
                      y: [-20, 20, -20],
                      opacity: [0.1, 0.3, 0.1],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-success/5 rounded-full blur-lg"
                  />
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
